// src/modules/app-views/_apps/appmulti/quotations/Form/Steps/CheckoutStep2.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Card, Col, Flex, Form, Row } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { isValidCpf } from 'utils/validation';
import { returnOnlyNumbers } from 'utils/helpers';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import SimpleCpf from 'components/SimpleComponents/SimpleCpf';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleAddressForm from 'components/SimpleComponents/SimpleEntityForm/SimpleAddressForm';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import entitiesService from 'services/entitiesService';
import { API } from 'constants/Api';
import axiosService from 'services/axiosService';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const actionBtnStyle = { width: 130 };

// *******************************************************************************
// COMPONENT — body (inside SimpleFormProvider)
// *******************************************************************************
const Step2Body = props => {
  // HOOKS
  const { form } = useSimpleFormContext();
  const {
    step,
    setStep,
    travellers,
    buyer,
    setBuyer,
    enabledSteps,
    setEnabledSteps,
    isMobile,
    setUpdating,
    isDisabled,
    registerOnFinish,
  } = props;

  const cpfWatch = Form.useWatch('cpf', form);

  // EFFECTS
  useEffect(() => {
    if (buyer) form?.setFieldsValue(buyer);
    else form?.resetFields();
  }, [buyer]); // eslint-disable-line react-hooks/exhaustive-deps

  // CPF-driven buyer lookup (replaces legacy FormCpf onChange)
  useEffect(() => {
    const cpf = returnOnlyNumbers(cpfWatch || '');
    if (!cpf || cpf === buyer?.cpf || !isValidCpf(cpf)) return;
    (async () => {
      const doc = await entitiesService.getEntityByField(cpf, 'cpf');
      if (!doc) return;
      setBuyer(doc);
      // phone/email use the subdocument shape { value, ... } (SimplePhone/SimpleEmail
      // bind to name={['phone'|'email','value']}); normalize string/subdoc to it.
      const phoneValue =
        typeof doc?.phone === 'object' ? doc?.phone : { value: doc?.phone };
      const emailValue =
        typeof doc?.email === 'object' ? doc?.email : { value: doc?.email };
      form.setFieldsValue({
        name: doc?.name,
        fullName: doc?.fullName,
        phone: phoneValue,
        email: emailValue,
        zip: doc?.zip,
        address1: doc?.address1,
        number: doc?.number,
        address2: doc?.address2 || null,
        neighborhood: doc?.neighborhood,
        city: doc?.city,
        region: doc?.region,
      });
    })();
  }, [cpfWatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 1) form.getFieldInstance?.('cpf')?.focus?.();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  const onPrevious = () => {
    if (isDisabled) return setStep(0);
    setBuyer({ ...form.getFieldsValue() });
    setStep(0);
  };

  const onNext = async values => {
    if (isDisabled) {
      setStep(2);
      return false;
    }
    setUpdating(true);
    const data = {
      _id: buyer?._id,
      dataType: 'person',
      name: buyer?.name || values?.name,
      ...values,
    };
    if (!data?.address2) data.address2 = null;

    const buyerId = buyer?._id;
    const url = API.ENTITIES.URL.replace(':_id?', buyerId || '');
    const payload = { ...data, dataProfile: 'customer' };
    delete payload._id;
    const [doc] = buyerId
      ? await axiosService.put({ url, data: payload })
      : await axiosService.post({ url, data: payload });

    setBuyer(doc);
    if (enabledSteps && !enabledSteps.includes(2)) {
      setEnabledSteps([...enabledSteps, 2]);
    }
    setStep(2);
    return false; // abort provider auto-save
  };

  // register the form-finish handler for the provider
  registerOnFinish?.(onNext);

  // RENDER
  return (
    <>
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} lg={12}>
          <Card className="w-100 strong-border">
            <Row gutter={ROW_GUTTER}>
              <Col xs={24} md={10}>
                {/* eslint-disable-next-line jsx-a11y/no-autofocus -- legacy parity: CPF auto-focus on open */}
                <SimpleCpf name="cpf" disabled={isDisabled} required autoFocus />
              </Col>
              <Col xs={24} sm={14}>
                <SimpleInput
                  label="name_social"
                  name="name"
                  required
                  onBlur={() => {
                    const nameValue = form.getFieldValue('name');
                    const fullNameValue = form.getFieldValue('fullName');
                    if (nameValue && !fullNameValue) {
                      form.setFieldValue('fullName', nameValue);
                    }
                  }}
                />
              </Col>
              <Col xs={24} sm={24}>
                <SimpleInput label="full_name" name="fullName" />
              </Col>
            </Row>
            <Row gutter={ROW_GUTTER}>
              <Col xs={24} md={10}>
                <SimplePhone
                  name={['phone', 'value']}
                  label="phone"
                  disabled={isDisabled}
                  required
                />
              </Col>
              <Col xs={24} md={14}>
                <SimpleEmail
                  name={['email', 'value']}
                  label="email"
                  disabled={isDisabled}
                  required
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card className="w-100 strong-border">
            <SimpleAddressForm />
          </Card>
        </Col>
      </Row>

      {!isDisabled ? (
        <Flex justify={isMobile ? 'center' : 'flex-end'} align="center">
          <SimpleButton
            title="previous"
            className="mr-3"
            icon={<ArrowLeftOutlined />}
            style={actionBtnStyle}
            onClick={onPrevious}
          />
          <SimpleButton
            title="next"
            type={travellers?.length > 0 ? 'primary' : 'default'}
            icon={<ArrowRightOutlined />}
            style={actionBtnStyle}
            onClick={form.submit}
          />
        </Flex>
      ) : null}
    </>
  );
};

// *******************************************************************************
// COMPONENT — main
// *******************************************************************************
const MultiQuotationsCheckoutStep2 = props => {
  let onFinishHandler = async () => false;
  const registerOnFinish = fn => {
    onFinishHandler = fn;
  };

  return (
    <SimpleFormProvider
      name="form-buyer"
      showSubmitButton={false}
      requiredFields={['cpf', 'name', 'phone', 'email']}
      onFinish={({ values }) => onFinishHandler(values)}
      initialValues={props.buyer || {}}
    >
      <Step2Body {...props} registerOnFinish={registerOnFinish} />
    </SimpleFormProvider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationsCheckoutStep2;
